import * as bi from 'react-bootstrap-icons';
import {
  IClientPassengerBrief,
  IClientPaymentTicketsBookingPost,
  IClientTripFull,
  IClientTripPriceCalculationFull,
} from '@bustickus/models-client';
import { Box, Button, Checkbox, Flex, Loader, Message } from 'components';
import { Icon } from 'shared/components';
import { handleAuthError } from 'common/handlers';
import { ApiPayment } from 'common/api';
import { useEffect, useState } from 'react';
import { TicketCalculation } from './components';

interface IProps {
  trip: IClientTripFull | null | undefined;
  back: IClientTripFull | null | undefined;
  dCityId: string | null | undefined;
  aCityId: string | null | undefined;
  passengers: IClientPassengerBrief[];
  calculations: IClientTripPriceCalculationFull | null;
}

function PaymentBlock(props: IProps) {
  const initialPassportConfirmed = props.calculations?.needPassportConfirmation === true ? false : true;
  const initialForeignPassportConfirmed = props.calculations?.needForeignPassportConfirmation === true ? false : true;
  const initialVisaConfirmed = props.calculations?.needVisaConfirmation === true ? false : true;

  const [loading, setLoading] = useState(props.calculations == null && props.passengers.length > 0);
  const [passportConfirmed, setPassportConfirmed] = useState<boolean>(initialPassportConfirmed);
  const [foreignPassportConfirmed, setForeignPassportConfirmed] = useState<boolean>(initialForeignPassportConfirmed);
  const [visaConfirmed, setVisaConfirmed] = useState<boolean>(initialVisaConfirmed);

  useEffect(() => {
    const initialPassportConfirmed = props.calculations?.needPassportConfirmation === true ? false : true;
    const initialForeignPassportConfirmed = props.calculations?.needForeignPassportConfirmation === true ? false : true;
    const initialVisaConfirmed = props.calculations?.needVisaConfirmation === true ? false : true;

    setPassportConfirmed(initialPassportConfirmed);
    setForeignPassportConfirmed(initialForeignPassportConfirmed);
    setVisaConfirmed(initialVisaConfirmed);
  }, [props.calculations]);

  const handlePayClick = async () =>
    handleAuthError(async () => {
      setLoading(true);

      const trips: { [tripId: string]: IClientPaymentTicketsBookingPost[] } = {};

      if (props.trip != null) {
        trips[props.trip.id] = createPaymentTickets(
          props.dCityId ?? props.trip.points[0].cityId ?? '',
          props.aCityId ?? props.trip.points[props.trip.points.length - 1].cityId ?? ''
        );
      }

      if (props.back != null) {
        trips[props.back.id] = createPaymentTickets(
          props.aCityId ?? props.trip?.points[props.trip.points.length - 1].cityId ?? '',
          props.dCityId ?? props.trip?.points[0].cityId ?? ''
        );
      }

      const apiPayment = ApiPayment.getInstance();
      const response = await apiPayment.buy(trips);
      window.location.href = response.paymentUrl;
    });

  const createPaymentTickets = (dCityId: string, aCityId: string): IClientPaymentTicketsBookingPost[] => {
    const paymentTickets: IClientPaymentTicketsBookingPost[] = props.passengers.map((x) => {
      const paymentTicket: IClientPaymentTicketsBookingPost = {
        departureCityId: dCityId,
        arrivalCityId: aCityId,
        passengerId: x.id,
      };

      return paymentTicket;
    });

    return paymentTickets;
  };

  const isError = props.calculations?.error != null || props.passengers.length === 0;

  const isErrorPassengersEmpty = props.passengers.length === 0;
  const isTripError = !props.trip?.isAvailable || (props.back != null && !props.back.isAvailable);

  const priceString = new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB' }).format(
    props.calculations?.price ?? 0
  );

  const confirmed = passportConfirmed && visaConfirmed && foreignPassportConfirmed;

  return (
    <Flex
      direction='column'
      gap={10}
    >
      {isError && (
        <Message
          type='error'
          title='Ошибка'
        >
          {isErrorPassengersEmpty && <ErrorMessage text='Не выбран ни один пассажир' />}
          {props.calculations?.error === 'not_enough_tickets' && (
            <ErrorMessage text='Пассажиров выбрано больше, чем доступно билетов на этот рейс' />
          )}
          {props.calculations?.error === 'trip_archived' && (
            <ErrorMessage text='Невозможно купить билеты на архивный рейс' />
          )}
          {props.calculations?.error === 'too_late' && (
            <ErrorMessage text='Этот рейс скоро отправляется, на него закрыта продажа билетов' />
          )}
        </Message>
      )}
      <Box padding={20}>
        <Flex
          direction='column'
          gap={10}
        >
          {props.calculations != null && props.calculations.tickets.length > 0 && (
            <Message type='error'>
              <Flex
                direction='column'
                gap={10}
              >
                {props.calculations.tickets.map((x) => (
                  <TicketCalculation
                    key={`${x.tripRouteTitle} - ${x.passengerName}`}
                    calculation={x}
                  />
                ))}
              </Flex>
            </Message>
          )}

          {(props.calculations?.needPassportConfirmation === true ||
            props.calculations?.needForeignPassportConfirmation === true ||
            props.calculations?.needVisaConfirmation === true) &&
            props.calculations?.error == null && (
              <Message
                type='error'
                title='Требуется подтверждение'
              >
                <Flex
                  direction='column'
                  gap={10}
                >
                  <Flex
                    direction='column'
                    gap={0}
                  >
                    <div>
                      Один или несколько пассажиров не имеют необходимых документов для покупки билета на этот рейс,
                      либо эти документы просрочены или будут просрочены на момент отправления рейса.
                    </div>

                    <div>
                      Если вы берёте на себя ответственность за это, согласитесь с утверждениями ниже, поставив
                      необходимые галочки.
                    </div>
                  </Flex>

                  {props.calculations?.needPassportConfirmation === true && (
                    <Checkbox
                      checked={passportConfirmed}
                      onChange={() => setPassportConfirmed(!passportConfirmed)}
                    >
                      У одного или нескольких пассажиров не указан паспорт, я понимаю, что пассажира могут не допустить
                      или высадить с рейса
                    </Checkbox>
                  )}
                  {props.calculations?.needForeignPassportConfirmation === true && (
                    <Checkbox
                      checked={foreignPassportConfirmed}
                      onChange={() => setForeignPassportConfirmed(!foreignPassportConfirmed)}
                    >
                      У одного или нескольких пассажиров не указан или просрочен заграничный паспорт, я понимаю, что
                      пассажира могут не допустить на рейс, высадить с него или не позволить пересечь государственную
                      границу
                    </Checkbox>
                  )}
                  {props.calculations?.needVisaConfirmation === true && (
                    <Checkbox
                      checked={visaConfirmed}
                      onChange={() => setVisaConfirmed(!visaConfirmed)}
                    >
                      У одного или нескольких пассажиров отсутсвует или просрочена необходимая для этого рейса виза, я
                      понимаю, что пассажира могут не допустить на рейс, высадить с него или не позволить перейти
                      государственную границу
                    </Checkbox>
                  )}
                </Flex>
              </Message>
            )}

          {props.calculations != null && props.calculations.error == null && (
            <Flex gap={5}>
              <Icon color='red'>
                <bi.Cash />
              </Icon>
              <span>К оплате:</span>
              <b>{priceString}</b>
            </Flex>
          )}

          <Button
            color={isTripError ? 'transparent-red' : 'red'}
            disabled={isTripError || isError || loading || !confirmed}
            onClick={handlePayClick}
          >
            <Flex
              justifyContent='center'
              alignItems='center'
              gap={5}
            >
              {loading && (
                <Loader
                  variant='line'
                  color='background-02'
                  size='default'
                />
              )}

              {!loading && (isTripError || isError) && (
                <Flex
                  gap={5}
                  justifyContent='center'
                  alignItems='center'
                >
                  <bi.Ban />
                  <span>Покупка недоступна</span>
                </Flex>
              )}

              {!loading && !isTripError && !isError && confirmed && (
                <Flex
                  gap={5}
                  justifyContent='center'
                  alignItems='center'
                >
                  <bi.Coin />
                  <span>Оплатить</span>
                </Flex>
              )}

              {!loading && !isTripError && !isError && !confirmed && (
                <Flex
                  gap={5}
                  justifyContent='center'
                  alignItems='center'
                >
                  <bi.Coin />
                  <span>Для оплаты подтвердите документы</span>
                </Flex>
              )}
            </Flex>
          </Button>
        </Flex>
      </Box>
    </Flex>
  );
}

function ErrorMessage(props: { text: string }) {
  return (
    <Flex gap={5}>
      <Icon color='red'>
        <bi.X />
      </Icon>
      <span>{props.text}</span>
    </Flex>
  );
}

export default PaymentBlock;
